// ============================================================================
// BOQ — bill build-up (prelims / contingency / tax / deductions)
// ============================================================================
// Ordered adjustments applied on top of net works to reach the grand total.
// computeBuildUp() turns them into a waterfall on the estimate + tender bases.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { formatSupabaseError } from '../../hooks/useSupabase';

const mapAdj = (a) => ({
  id: a.id, label: a.label, value: Number(a.value),
  valueType: a.value_type, base: a.base, sign: a.sign, sortOrder: a.sort_order, note: a.note,
});

// Currency-based default build-up (INR → India GST; EUR → EU VAT).
export function defaultBuildUp(currency) {
  if (currency === 'EUR') {
    return [
      { label: 'Preliminaries', value: 8, valueType: 'percent', base: 'net', sign: 'add' },
      { label: 'Contingency', value: 5, valueType: 'percent', base: 'running', sign: 'add' },
      { label: 'VAT', value: 21, valueType: 'percent', base: 'running', sign: 'add' },
    ];
  }
  return [
    { label: 'Preliminaries', value: 8, valueType: 'percent', base: 'net', sign: 'add' },
    { label: 'Contingency', value: 5, valueType: 'percent', base: 'running', sign: 'add' },
    { label: 'GST', value: 18, valueType: 'percent', base: 'running', sign: 'add' },
  ];
}

// netQuoted / netBilled are the net works totals (measured + provisional).
export function computeBuildUp(netQuoted, netBilled, adjustments) {
  let rQ = Number(netQuoted) || 0;
  let rB = Number(netBilled) || 0;
  const rows = [...adjustments].sort((a, b) => a.sortOrder - b.sortOrder).map((a) => {
    const baseQ = a.base === 'net' ? (Number(netQuoted) || 0) : rQ;
    const baseB = a.base === 'net' ? (Number(netBilled) || 0) : rB;
    const dQ = a.valueType === 'percent' ? baseQ * a.value / 100 : a.value;
    const dB = a.valueType === 'percent' ? baseB * a.value / 100 : a.value;
    const s = a.sign === 'deduct' ? -1 : 1;
    rQ += s * dQ; rB += s * dB;
    return { ...a, deltaQuoted: s * dQ, deltaBilled: s * dB, runningQuoted: rQ, runningBilled: rB };
  });
  return { rows, grandQuoted: rQ, grandBilled: rB };
}

export const useBoqBillAdjustments = (boqProjectId) => {
  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!boqProjectId) { setAdjustments([]); setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      const { data, error: fErr } = await supabase.schema('boq').from('bill_adjustments')
        .select('id, label, value, value_type, base, sign, sort_order, note')
        .eq('project_id', boqProjectId).order('sort_order');
      if (fErr) throw fErr;
      setAdjustments((data || []).map(mapAdj));
    } catch (err) {
      setError(formatSupabaseError(err, { action: 'fetch', entity: 'build-up' }));
    } finally { setLoading(false); }
  }, [boqProjectId]);

  const addAdjustment = useCallback(async (input) => {
    try {
      const nextOrder = adjustments.reduce((m, a) => Math.max(m, a.sortOrder), 0) + 1;
      const { data, error: insErr } = await supabase.schema('boq').from('bill_adjustments')
        .insert({
          project_id: boqProjectId, label: input.label?.trim() || 'Adjustment',
          value: Number(input.value) || 0, value_type: input.valueType || 'percent',
          base: input.base || 'running', sign: input.sign || 'add', sort_order: input.sortOrder ?? nextOrder,
        })
        .select('id, label, value, value_type, base, sign, sort_order, note').single();
      if (insErr) throw insErr;
      setAdjustments((prev) => [...prev, mapAdj(data)].sort((a, b) => a.sortOrder - b.sortOrder));
      return { success: true };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'add', entity: 'adjustment' }) };
    }
  }, [boqProjectId, adjustments]);

  const updateAdjustment = useCallback(async (id, patch) => {
    try {
      const payload = {};
      if (patch.label !== undefined) payload.label = patch.label;
      if (patch.value !== undefined) payload.value = Number(patch.value) || 0;
      if (patch.valueType !== undefined) payload.value_type = patch.valueType;
      if (patch.base !== undefined) payload.base = patch.base;
      if (patch.sign !== undefined) payload.sign = patch.sign;
      const { data, error: uErr } = await supabase.schema('boq').from('bill_adjustments')
        .update(payload).eq('id', id).select('id, label, value, value_type, base, sign, sort_order, note').single();
      if (uErr) throw uErr;
      setAdjustments((prev) => prev.map((a) => (a.id === id ? mapAdj(data) : a)));
      return { success: true };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'adjustment' }) };
    }
  }, []);

  const deleteAdjustment = useCallback(async (id) => {
    try {
      const { error: dErr } = await supabase.schema('boq').from('bill_adjustments').delete().eq('id', id);
      if (dErr) throw dErr;
      setAdjustments((prev) => prev.filter((a) => a.id !== id));
      return { success: true };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'adjustment' }) };
    }
  }, []);

  const applyDefaults = useCallback(async (currency) => {
    const defs = defaultBuildUp(currency);
    for (let i = 0; i < defs.length; i++) {
      // eslint-disable-next-line no-await-in-loop
      await addAdjustment({ ...defs[i], sortOrder: (adjustments.length + i + 1) });
    }
    return { success: true };
  }, [addAdjustment, adjustments.length]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return { adjustments, loading, error, refresh: fetchAll, addAdjustment, updateAdjustment, deleteAdjustment, applyDefaults };
};
